import { createMemo, type JSX } from "solid-js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { JobCategory } from "@/domains/job-offer/create/constants";
import {
  type CategoryOption,
  categoryLabel,
  categoryOption,
  categoryOptions,
} from "../store/taxonomy-store";

type CategorySelectProps = {
  /** The English key held by the form, the filter or the URL. */
  value: string | null | undefined;
  /** Gets the English key of the chosen option, or `null` when cleared. */
  onChange: (key: JobCategory | null) => void;
  placeholder?: JSX.Element;
  name?: string;
  /** Class of the root of the select. */
  class?: string;
  triggerClass?: string;
};

/**
 * The single-value select of the 8 job categories: the options and the closed
 * field show the Italian label, `onChange` gets the English key.
 *
 * Options are objects, not strings, because Kobalte takes the text of a string
 * option from the string itself and ignores `optionTextValue`: typing the first
 * letters of a label would still search the English key.
 *
 * The closed field is rendered from `props.value`, never from the selected
 * option. Kobalte looks the selected keys up in `options` and drops those with
 * no match, but decides "empty" from the raw keys: a key outside the 8 (a
 * hand-typed `?job_category=`) would give an empty field with no placeholder.
 *
 * Kobalte also reports the selection again, as if the user had made it, every
 * time `options` changes, which here is when the tree arrives: the same key for
 * a key of the 8, `null` for an empty value or a key outside the 8. Only a
 * change of the value reaches `onChange`, and `null` only when the value is one
 * of the options (choosing the chosen option again clears it, as it always did).
 *
 * See `brain/specs/freelance/taxonomy-labels/PLAN.md` (FE4).
 */
export const CategorySelect = (props: CategorySelectProps) => {
  const options = createMemo(() => categoryOptions());

  const isUserChange = (key: string | null) => {
    const current = props.value || null;
    if (key === current) return false;
    return key !== null || options().some((option) => option.value === current);
  };

  const handleChange = (option: CategoryOption | null) => {
    const key = option?.value ?? null;
    if (isUserChange(key)) props.onChange(key as JobCategory | null);
  };

  return (
    <Select<CategoryOption>
      class={props.class}
      name={props.name}
      options={options()}
      optionValue="value"
      optionTextValue="label"
      value={props.value ? categoryOption(props.value) : null}
      onChange={handleChange}
      placeholder={props.placeholder}
      itemComponent={(item) => (
        <SelectItem item={item.item}>{item.item.rawValue.label}</SelectItem>
      )}
    >
      <SelectTrigger class={props.triggerClass}>
        <SelectValue>
          <span>{categoryLabel(props.value ?? "")}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent />
    </Select>
  );
};
